import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AccessTokenService } from './accesstoken.service';
import { OAuthService } from 'angular-oauth2-oidc';

export type CompanyType = 'crossPoint' | 'dealer' | 'installer' | 'retailer';

export const CompanyType = {
    CrossPoint: 'crossPoint' as CompanyType,
    Dealer: 'dealer' as CompanyType,
    Installer: 'installer' as CompanyType,
    Retailer: 'retailer' as CompanyType
};

// We can't easily type the permissions type because of the way this is reused 
// between portal and devex with their own permissions, so we use <any> instead
export type PermissionCheck  = (p: any) => boolean | null;

export interface IAuthInfo {
  userId: string;
  impUserId?: string;
  companyType: CompanyType;
  companyId: string;
  linkedCompanies: string[];
  roles: string[];
  permissions?: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  
  private static readonly cpClaimPrefix = "cp/";
  private jwtHelper: JwtHelperService;

  constructor(
    private httpClient: HttpClient, 
    private accessTokenService: AccessTokenService,
    private oAuthService: OAuthService
  ) {
    this.jwtHelper = new JwtHelperService();
  }

  public login(): Observable<string> {
    return this.httpClient.get<string>('/api/v1/auth/accessToken');
  }

  public logout(): void {
    this.oAuthService.logOut();
    
    // Clear all the browser cache
    localStorage.clear();
    sessionStorage.clear();
  }

  public getAccessTokenInfo(accessToken: string) {
    const decodedToken = this.jwtHelper.decodeToken(accessToken);
    return {
      sub: decodedToken["sub"],
      companyId: decodedToken[AuthService.cpClaimPrefix + "company_id"],
      companyType: AuthService.companyType(decodedToken[AuthService.cpClaimPrefix + "company_type"]),
      linkedCompanies:  AuthService.asArray(decodedToken[AuthService.cpClaimPrefix + "linked_company"]),
      roles: AuthService.asArray(decodedToken["role"]),
      scopes: AuthService.asArray(decodedToken["scope"]),
      clientId: decodedToken["client_id"],
      audiences: AuthService.asArray(decodedToken["aud"]),
      impUserId:  decodedToken[AuthService.cpClaimPrefix + "imp_user_id"],
    }
  }

  public getIdentityTokenInfo(idToken: string) {
    const decodedToken = this.jwtHelper.decodeToken(idToken);
    return {
      sub: decodedToken["sub"],
      name: decodedToken["name"],
      email: decodedToken["email"],
      companyId: decodedToken[AuthService.cpClaimPrefix + "company_id"],
      companyType: AuthService.companyType(decodedToken[AuthService.cpClaimPrefix + "company_type"]),
      companyName: decodedToken[AuthService.cpClaimPrefix + "company_name"],
      impUserId:  decodedToken[AuthService.cpClaimPrefix + "imp_user_id"],
      impName:  decodedToken[AuthService.cpClaimPrefix + "imp_name"],
      impEmail:  decodedToken[AuthService.cpClaimPrefix + "imp_email"],
    }
  }

  public isAuthenticated(): boolean {
    const accessTokenExpiration: number = this.jwtHelper.getTokenExpirationDate(this.accessTokenService.currentAccessToken).getTime();
    return (accessTokenExpiration && accessTokenExpiration > Date.now());
  }

  private static asArray(item: string | string[] | null | undefined) {
    if (!item) {
      return [];
    }
    return Array.isArray(item) ? item : [item];
  }

  private static companyType(companyType: string): CompanyType {
    switch (companyType) {
    case "crosspoint": return 'crossPoint';
    case "dealer": return 'dealer';
    case "installer": return 'installer';
    case "retailer": return 'retailer';
    }
    throw new Error("Unknown company type: " + companyType);
  }
}
